/**
 * Every handled-error code the TypeScript app can raise.
 *
 * This list exists so the presentation registry can be *exhaustive*: it is the
 * `Record` key set that `presentation.ts` must satisfy, which turns "someone
 * added an error code and never wrote the copy" from a runtime shrug into a
 * `pnpm typecheck` failure.
 *
 * Kept honest by `__tests__/codes.unit.test.ts`, which scans `src/`, `ee/` and
 * the workspace `packages/` for every code a `HandledError` subclass declares
 * and fails when this list and the code drift apart in either direction. All
 * three trees raise handled errors, and a scan that covers only one is a list
 * that silently stops being exhaustive. Adding a code here without
 * throwing it anywhere is just as much a bug as the reverse — it means dead
 * copy, which is how `recipient_not_in_team` survived in the automations
 * explainer for a code nothing raises.
 *
 * Codes declared by the Go services are NOT here — they come from
 * `goErrorCodes` in `@langwatch/handled-error`, generated by `cmd/herrgen`.
 */
export const APP_ERROR_CODES = [
  "agent_report_rate_limited",
  "ai_call_failed",
  "ai_query_provider_error",
  "already_organization_member",
  "anomaly_rule_not_found",
  "api_key_already_revoked",
  "api_key_not_found",
  "api_key_not_in_organization",
  "api_key_not_owned",
  "api_key_permission_denied",
  "api_key_reserved_name",
  "api_key_scope_violation",
  "avatar_image_processing_failed",
  "avatar_image_too_large",
  "avatar_image_type_unsupported",
  "avatar_image_unreadable",
  "avatar_rate_limited",
  "billing_currency_unsupported",
  "billing_customer_deleted",
  "billing_customer_email_required",
  "billing_plan_price_missing",
  "billing_provider_unavailable",
  "billing_quote_expired",
  "broadcaster_not_active",
  "budget_not_found",
  "cache_rule_not_found",
  "cannot_demote_last_admin",
  "cannot_disable_last_admin",
  "cannot_disable_self",
  "cannot_impersonate_admin",
  "cannot_impersonate_deactivated_user",
  "cannot_remove_last_admin",
  "cannot_remove_self",
  "cannot_remove_self_as_last_admin",
  "clickhouse_overloaded",
  "clickhouse_unavailable",
  "codex_auth_failed",
  "credential_class_mismatch",
  "custom_role_id_required",
  "custom_role_in_use",
  "custom_role_name_reserved",
  "custom_role_name_taken",
  "custom_role_not_assignable",
  "custom_role_not_found",
  "dataset_column_type_change_unsupported",
  "dataset_name_taken",
  "dataset_not_ready",
  "dataset_stale_columns",
  "dspy_step_not_found",
  "duplicate_invite",
  "email_already_registered",
  "enterprise_plan_required",
  "evaluation_not_found",
  "evaluator_config_error",
  "evaluator_execution_error",
  "evaluator_input_too_large",
  "evaluator_missing_field",
  "evaluator_not_found",
  "experiment_not_found",
  "export_failed",
  "external_id_conflict",
  "filter_field_unknown",
  "filter_parse_error",
  "gateway_budget_cycle_anchor_invalid",
  "gateway_budget_not_found",
  "gateway_budget_scope_unreachable",
  "gateway_group_budget_unsupported",
  "gateway_guardrail_project_mismatch",
  "gateway_provider_bindings_gone",
  "gateway_scope_org_mismatch",
  "gateway_spend_group_by_unstable",
  "gateway_spend_unavailable",
  "gateway_trace_project_ambiguous",
  "gateway_trace_project_unknown",
  "github_installation_suspended",
  "github_not_connected",
  "github_pr_not_mapped",
  "github_rate_limited",
  "github_repo_not_accessible",
  "group_member_already_added",
  "group_not_found",
  "group_not_in_organization",
  "guardrail_attach_forbidden",
  "idempotency_error",
  "ingestion_source_cap_reached",
  "ingestion_source_not_found",
  "insufficient_permissions",
  // Also a Go code, with copy already written under the shared/transport
  // heading — the REST boundary in `packages/api` puts it on the wire as the
  // code for an unhandled failure, so the scan sees it here too.
  "internal_error",
  "invalid_action_params",
  "invalid_credentials",
  "invalid_cursor",
  "invalid_email_recipient",
  "invalid_experiment_configuration",
  "invite_not_found",
  "langy_agent_at_capacity",
  "langy_agent_errored",
  "langy_agent_session_lost",
  "langy_agent_unavailable",
  "langy_codex_plan_limit",
  "langy_conversation_not_found",
  "langy_conversation_not_owned",
  "langy_credential_resolution",
  "langy_dispatch_rejected",
  "langy_egress_misconfigured",
  "langy_empty_message",
  "langy_github_not_connected",
  "langy_github_repo_not_accessible",
  "langy_idempotency_mismatch",
  "langy_insufficient_scope",
  "langy_model_not_allowed",
  "langy_model_not_configured",
  "langy_not_enabled",
  "langy_rate_limited",
  "langy_turn_in_progress",
  "langy_turn_not_stoppable",
  "langy_turn_timeout",
  "langy_worker_restarting",
  "langy_worker_spawn_failed",
  "langy_worker_stopped",
  "license_expired",
  "license_key_invalid",
  "license_signing_failed",
  "license_signing_key_encrypted",
  "license_signing_key_not_pem",
  "lite_member_restricted",
  "lite_member_viewer_only",
  "malformed_custom_role_permissions",
  "malformed_request",
  "member_not_found",
  "member_seat_limit_reached",
  "missing_annotator",
  "missing_credentials",
  // Not declared by a subclass anywhere: `nlpgo/goHandledError.ts` promotes a
  // relayed Go error's `meta.reason` to the code, so this one is minted at the
  // boundary rather than thrown. It still reaches a customer, so it still needs
  // copy — see RELAYED_META_CODES in `__tests__/codes.unit.test.ts`.
  "missing_provider",
  "missing_slack_bot_token",
  "missing_slack_webhook",
  "model_not_configured",
  "model_provider_anchor_required",
  "model_provider_credentials_would_be_dropped",
  "model_provider_deprecated",
  "model_provider_disabled",
  "model_provider_not_found",
  "model_provider_scope_forbidden",
  "model_provider_scopes_required",
  "model_provider_test_rate_limited",
  "model_restricted_for_feature",
  "monitor_evaluator_required",
  "no_admin_configured",
  // Also a Go code, with copy already written under the shared/transport
  // heading — `ee/admin/routes/admin.ts` raises it to hide the admin surface
  // from a session that is no longer an admin.
  "not_found",
  "notification_delivery_error",
  "org_exclusive_permission_scope",
  "organization_not_found",
  "organization_not_found_for_team",
  "organization_slug_taken",
  "page_too_deep",
  "personal_project_key_required",
  "personal_usage_key_mismatch",
  "personal_workspace_not_managed_here",
  "project_not_found",
  "project_permission_denied",
  "prompt_not_found",
  "provider_endpoint_redirected",
  "provider_key_invalid",
  "provider_key_missing",
  "provider_key_restricted",
  "provider_refused",
  "provider_service_disabled",
  "provider_unreachable",
  "published_workflow_version_not_found",
  "query_memory_exceeded",
  "query_timeout",
  "resource_limit_exceeded",
  "role_binding_already_exists",
  "role_binding_not_found",
  "role_binding_principal_invalid",
  "rum_ingest_disabled",
  "rum_payload_invalid",
  "rum_payload_too_large",
  "rum_rate_limited",
  "run_not_found",
  "scenario_run_export_forbidden",
  "scenario_run_export_unauthenticated",
  "schema_failure",
  "scim_managed_group",
  "scim_token_not_found",
  "scope_not_in_organization",
  "seat_billing_unavailable",
  "share_link_exhausted",
  "share_link_expired",
  "share_link_forbidden",
  "share_link_not_found",
  "share_read_rate_limited",
  "span_not_found",
  "spend_source_unavailable",
  "subscription_ambiguous",
  "subscription_not_linked",
  "subscription_service_unavailable",
  "subscription_sync_failed",
  "suite_all_scenarios_archived",
  "suite_all_targets_archived",
  "suite_invalid_scenario_references",
  "suite_invalid_target_references",
  "suite_name_taken",
  "suite_not_found",
  "system_prompt_conflict",
  "system_prompt_required",
  "team_last_admin_required",
  "team_member_already_added",
  "team_membership_not_found",
  "team_name_taken",
  "team_not_found",
  "team_not_in_organization",
  "template_immutable",
  "template_not_found",
  "template_validation_error",
  "test_fire_unavailable",
  "time_range_too_wide",
  "trace_not_evaluatable",
  "trace_not_found",
  "trace_project_required",
  "trace_sharing_disabled",
  "trigger_filters_required",
  // Also a Go code, with copy already written under the shared/transport
  // heading — an app-level subclass raises it too (`ee/admin/routes/admin.ts`),
  // and the scan is right to insist it be listed here as well.
  "unauthorized",
  "usage_report_failed",
  "user_not_in_organization",
  "user_to_impersonate_not_found",
  // Raised by the shared package rather than an app-level subclass:
  // `ValidationError` (and `ValidationError.fromZodError`, which the tRPC
  // boundary uses to promote input ZodErrors onto the handled channel).
  "validation_error",
  "virtual_key_not_found",
  // The webhook platform names its two endpoint failures itself, in
  // `~/app/api/webhooks/[[...route]]/error-handler`, rather than deriving
  // them from the status: `not_found` alone would not say WHICH lookup
  // missed, and an endpoint id is the only one those routes take.
  "webhook_endpoint_invalid",
  "webhook_endpoint_not_found",
  "webhook_event_not_found",
  "workflow_execution_failed",
  "workflow_not_found",
] as const;

export type AppErrorCode = (typeof APP_ERROR_CODES)[number];
